/**
 * composeModuleRoutes — mounts module-contributed routes under the
 * tenant-scoped parent route at boot.
 *
 * Per KEN_LAYER_7 §7.3 + ADR-040:
 *   - Module routes are NOT in the cockpit's file-based src/routes/ tree.
 *   - vite-plugin-ken-modules generates src/generated/modules.ts at build
 *     time with the validated MODULES array.
 *   - This helper iterates MODULES, wraps each module's subtree in a
 *     per-module ErrorBoundary, optionally applies the module's Provider,
 *     and adds the result as children of the /$tenantSlug parent route.
 *
 * Failure isolation (KEN_LAYER_7 Principle 9): a runtime throw in one
 * module's component never crashes the shell — the ErrorBoundary catches
 * it and renders a fallback. The rest of the cockpit (nav, Concierge,
 * dashboard) stays usable.
 *
 * Screens on demand (ADR-040, BND-01): modules hand their screens over as
 * `lazyScreen(...)` components from @ken/cockpit-types, so a screen's code
 * downloads the first time it is opened. This helper keeps that working
 * through its wrapping: the wrapper carries the screen's `preload` (the
 * router fetches the chunk while it resolves the match, before it renders),
 * every module route gets the shared pending state for a slow chunk, and a
 * chunk that fails to load surfaces in the module's ErrorBoundary — tab
 * routes nested under a detail page have no boundary of their own, so their
 * failures reach the same one through the parent's outlet.
 */

import { useAuth } from "@ken/auth";
import type { ModuleRegistration } from "@ken/cockpit-types";
import { StateLoading } from "@ken/ui";
import type { AnyRoute } from "@tanstack/react-router";
import { Component, Suspense, type ErrorInfo, type ReactElement, type ReactNode } from "react";

import { MODULES } from "../generated/modules";
import { reportError } from "./installErrorCapture";

/**
 * Phase 0b.2 — Renders the module's component only when the active session
 * lists ``slug`` in ``enabledModules``. Otherwise renders a "Module disabled"
 * placeholder so a tenant with the module switched off can't reach the
 * module's screens via direct URL. The backend already 403s data calls
 * (see ``require_module`` in layer7/router.py); this gate provides the UI
 * affordance.
 */
function ModuleGate(props: { slug: string; children: ReactNode }): ReactElement {
  const { session } = useAuth();
  const enabled = session?.enabledModules ?? [];
  if (!enabled.includes(props.slug)) {
    return (
      <div
        role="alert"
        data-testid={`module-disabled-${props.slug}`}
        className="m-6 rounded-[var(--radius-md)] border border-[var(--color-glass-border)] bg-[var(--color-glass-surface)] p-6 text-sm"
      >
        <h2 className="text-base font-semibold text-[var(--color-text-strong)] mb-1">
          This module is turned off
        </h2>
        <p className="text-[var(--color-text-soft)]">
          The <strong>{props.slug}</strong> module isn&apos;t enabled for this
          tenant. An owner can switch it on under Settings → Modules.
        </p>
      </div>
    );
  }
  return <>{props.children}</>;
}

interface ModuleErrorBoundaryProps {
  moduleName: string;
  children: ReactNode;
}

interface ModuleErrorBoundaryState {
  error: Error | null;
}

class ModuleErrorBoundary extends Component<ModuleErrorBoundaryProps, ModuleErrorBoundaryState> {
  override state: ModuleErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ModuleErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Rewire into the unified error-collection pipeline (ADR-0121). This
    // marks the Error in `reportedErrors` so the window `error` listener
    // skips it — a boundary-caught crash is reported exactly once. The
    // component stack is appended to the JS stack for triage.
    reportError({
      title: error.message || `Module crash: ${this.props.moduleName}`,
      stack: [error.stack ?? "", (info.componentStack ?? "").trim()]
        .filter(Boolean)
        .join("\n\n"),
      error,
    });
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          data-testid={`module-runtime-error-${this.props.moduleName}`}
          className="m-6 rounded-[var(--radius-md)] border border-[var(--color-accent-red)] bg-[oklch(99%_0.04_25_/_0.4)] p-6 text-sm"
        >
          <h2 className="text-base font-semibold text-[var(--color-text-strong)] mb-1">
            Couldn&apos;t load this module
          </h2>
          <p className="text-[var(--color-text-soft)]">
            The {this.props.moduleName} module hit a runtime error. The rest of the cockpit is still
            working. Try refreshing — if it keeps happening, contact support.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * The one pending state every module screen shows while its code downloads
 * (only visible when a chunk is slow: the router waits `pendingMs` first).
 */
export function ModuleScreenPending(): ReactElement {
  return (
    <div className="p-6">
      <StateLoading idPrefix="module-screen" rows={4} label="this screen" />
    </div>
  );
}

type RouteComponent = (() => ReactElement) & { preload?: () => Promise<void> };

interface ComposableRoute {
  options?: { component?: RouteComponent; pendingComponent?: unknown };
  children?: unknown;
}

/**
 * Gives a module route, and every route nested under it, the shared pending
 * state — unless the module chose its own. TanStack Router wraps a match in
 * a Suspense boundary only when the route has a pending component, so this
 * is also what catches a nested tab screen suspending on its chunk.
 */
function applyPendingState(route: ComposableRoute): void {
  if (route.options && route.options.pendingComponent === undefined) {
    route.options.pendingComponent = ModuleScreenPending;
  }
  const children = route.children;
  const list = Array.isArray(children)
    ? children
    : children && typeof children === "object"
      ? Object.values(children)
      : [];
  for (const child of list as ComposableRoute[]) applyPendingState(child);
}

/**
 * Calls each module's `createRoutes(parentRoute)` factory, wraps every
 * returned route's `component` in the per-module ErrorBoundary (and the
 * module's optional Provider), and returns the flat array of mountable
 * children for `parent.addChildren([...])`.
 *
 * The factory pattern keeps modules from having to import the cockpit's
 * `$tenantSlug` route directly — the cockpit owns its parent route and
 * passes it in.
 */
export function composeModuleRoutes(parentRoute: AnyRoute): AnyRoute[] {
  const children: AnyRoute[] = [];

  for (const registration of MODULES as readonly ModuleRegistration[]) {
    const { name, createRoutes, Provider } = registration;

    const moduleRoutes = createRoutes(parentRoute);

    for (const moduleRoute of moduleRoutes) {
      const composable = moduleRoute as unknown as ComposableRoute;
      applyPendingState(composable);

      const OriginalComponent = composable.options?.component;

      if (!OriginalComponent) {
        // A layout route with no own component — still pass through so its
        // descendants mount, but skip wrapping.
        children.push(moduleRoute);
        continue;
      }

      // CRITICAL: render the module's component as JSX (`<OriginalComponent />`),
      // not by calling it as a function. Function-call form invokes the
      // component synchronously outside the React reconciler, which means a
      // throw escapes before our ErrorBoundary can mount and catch it.
      // The JSX form goes through React's render pipeline, and any throw
      // surfaces in `componentDidCatch` exactly as KEN_LAYER_7 §9 I-9 requires.
      //
      // The Suspense sits INSIDE the boundary: a screen whose chunk is still
      // arriving shows the pending state, and one whose chunk failed throws
      // into ModuleErrorBoundary rather than past it.
      const wrappedComponent: RouteComponent = (): ReactElement => {
        const wrappedInBoundary = (
          <ModuleErrorBoundary moduleName={name}>
            <ModuleGate slug={name}>
              <Suspense fallback={<ModuleScreenPending />}>
                <OriginalComponent />
              </Suspense>
            </ModuleGate>
          </ModuleErrorBoundary>
        );
        if (Provider) {
          return <Provider>{wrappedInBoundary}</Provider>;
        }
        return wrappedInBoundary;
      };
      // Keep the screen's preload visible to the router, which calls
      // `options.component.preload()` while it resolves the match — so the
      // chunk is in before the first render and nothing flashes.
      if (typeof OriginalComponent.preload === "function") {
        wrappedComponent.preload = OriginalComponent.preload;
      }

      (composable.options as { component: RouteComponent }).component = wrappedComponent;
      children.push(moduleRoute);
    }
  }

  return children;
}
